<template>
  <BuilderWrapper
    title="Pie Chart"
    helpful-tip="The pie chart can be displayed in the sidebar and/or popup"
  >
    <div
      v-if="!exclude.includes('showHide')"
      class="pb-3"
    >
      <b-checkbox v-model="showPiechartSidebar">
        Show pie chart in sidebar
      </b-checkbox>
      <b-checkbox v-model="showPopupChart">
        Show pie chart in popup
      </b-checkbox>
      <b-checkbox
        v-if="showPiechartSidebar"
        v-model="hidePiechartOnRootSubroot"
      >
        Hide pie chart in {{ title(PATH_ROOT) }} and {{ title(PATH_SUBROOT) }} tabs
      </b-checkbox>
      <b-checkbox
        v-if="showPiechartSidebar || showPopupChart"
        v-model="hidePiechartOnZeroNegative"
      >
        Hide any rows with zero or negative values
      </b-checkbox>
    </div>

    <template v-if="showPiechartSidebar || showPopupChart">
      <LabelledInput
        label="Pie chart total label"
        helpful-tip="Customise the text of the pie chart total label"
      >
        <b-input
          id="totalLabel"
          v-model="totalLabel"
          :placeholder="totalLabelPlaceholder"
          type="text"
          data-cy="total-label-input"
        />
      </LabelledInput>

      <LabelledInput
        v-if="hasMultipleUniqueUnits"
        label="Pie chart unit"
        helpful-tip="Select the unit for the chart. Only fields with a matching unit will be shown."
      >
        <b-select
          v-model="pieUnit"
          :options="fieldUnitOptions"
        />
      </LabelledInput>

      <LabelledInput
        label="Pie chart fields"
        :helpful-tip="pieChartFieldsHelpfulTip"
      >
        <draggable
          v-model="sortedPieFields"
          handle=".drag-handle"
        >
          <PieItem
            v-for="(item, index) in sortedPieFields"
            :key="item.column"
            :item="item"
            :index="index"
            @toggleGraph="toggleGraph"
          />
        </draggable>
      </LabelledInput>

      <PieDonut
        class="my-3"
        @toggleGraph="toggleGraph"
      />

      <!-- Show a sample pie graph with table -->
      <GraphTable
        v-if="showGraph"
        :id="graphId"
        :items="graphItems"
        class="my-3"
      />
    </template>
  </BuilderWrapper>
</template>

<script lang="ts">

import Vue, { PropType } from 'vue'
import Component from 'vue-class-component'

// @ts-ignore uuid not having types
import { v4 as uuidv4 } from 'uuid'
import BuilderWrapper from '@/components/builder/UI/BuilderWrapper.vue'
import LabelledInput from '@/components/builder/UI/LabelledInput.vue'
import PieItem from '@/components/builder/PiechartSettings/PieItem.vue'
import GraphTable from '@/components/public/GraphTable/GraphTable.vue'
import PieDonut from '@/components/builder/PiechartSettings/PieDonut.vue'
import draggable from 'vuedraggable'
import { FieldTypes } from '@/types'
import { APP_CONFIG_KEY, PATH_ROOT, PATH_SUBROOT, TOTAL_STRING_LABEL, UNIT_TYPE_MONETARY } from '@/constants'

const components = {
  BuilderWrapper,
  PieItem,
  GraphTable,
  PieDonut,
  draggable,
  LabelledInput,
}
const props = {
  exclude: {
    type: Array as PropType<string[]>,
    default: () => [],
  },
}

// eslint-disable-next-line no-use-before-define
@Component<PiechartSettings>({ components })
export default class PiechartSettings extends Vue.extend({ props }) {
  selected = null
  showGraph = true
  graphId = 'pie-chart-settings-' + uuidv4()
  chartUnit: string | null = null
  PATH_ROOT = PATH_ROOT
  PATH_SUBROOT = PATH_SUBROOT

  get totalLabelPlaceholder () {
    return this.$typedStore.primary.app.config.appConfig.totalStringLabel ?? TOTAL_STRING_LABEL
  }

  get totalLabel () {
    return this.$typedStore.primary.app.config.appConfig.piechartTotalLabel ?? this.$typedStore.primary.app.config.appConfig.totalStringLabel
  }

  set totalLabel (newValue) {
    this.updateAppConfig('piechartTotalLabel', newValue)
  }

  get pieUnit () {
    if (this.chartUnit === null) {
      if (this.piechartItems.length > 0) {
        // Set the pieUnit to unit of the already selected piechartItems
        const firstField = this.fieldMap.find(({ column }) => column === this.piechartItems[0].column)
        if (firstField?.type === FieldTypes.MONETARY) {
          return UNIT_TYPE_MONETARY
        } else {
          return firstField?.unit || ''
        }
      } else {
        return this.fieldUnitOptions[0].value
      }
    }
    return this.chartUnit
  }

  set pieUnit (newUnit) {
    this.chartUnit = newUnit
    if (this.chartUnit !== null) {
      // Automatically select all items with new unit (but not on initial load)
      // Remove old pie chart items
      this.$typedStore.primary.app.updateConfig({
        configPath: ['piechartItems'],
        value: [],
      })
      // Add new ones
      this.fieldsMatchingPieUnit.forEach(({ column }) => this.$typedStore.visualisationApp.addPiechartItem(column))
    }
  }

  get fieldMap () {
    return this.$typedStore.primary.app.config.fieldMap
  }

  get uniqueNumericUnits () {
    return this.$typedStore.visualisationApp.uniqueNumericUnits
  }

  get hasMultipleUniqueUnits () {
    const numberOfUniqueNumericUnits = this.uniqueNumericUnits.length
    const numerOfMonetaryUnits = this.hasMonetaryUnit ? 1 : 0
    return numberOfUniqueNumericUnits + numerOfMonetaryUnits > 1
  }

  get hasMonetaryUnit () {
    return this.fieldMap.some(({ type }) => type === FieldTypes.MONETARY)
  }

  get fieldUnitOptions () {
    return [
      ...this.hasMonetaryUnit ? [{ value: UNIT_TYPE_MONETARY, text: '<Monetary>' }] : [],
      ...this.uniqueNumericUnits.map((unit) => ({
        value: unit,
        text: unit === '' ? '<No unit>' : unit,
      })),
    ]
  }

  get fieldsMatchingPieUnit () {
    return this.fieldMap.filter(({ unit, type }) => {
      if (type === FieldTypes.NUMERIC) {
        return unit === this.pieUnit
      }
      if (type === FieldTypes.MONETARY) {
        return this.pieUnit === UNIT_TYPE_MONETARY
      }
      return false
    })
  }

  get pieChartFieldsHelpfulTip () {
    return `Select which fields to display in the pie chart. Only numeric fields with unit ${this.pieUnit} are shown.`
  }

  get piechartItems () {
    return this.$typedStore.visualisationApp.piechartItems
  }

  get sortedPieFields () {
    return this.fieldsMatchingPieUnit
      .slice(0)
      .sort((a, b) => {
        let aIndex = this.piechartItems.findIndex(i => i.column === a.column)
        let bIndex = this.piechartItems.findIndex(i => i.column === b.column)
        // Sort any items that are not in the piechartItems array to the bottom
        aIndex = aIndex === -1 ? Infinity : aIndex
        bIndex = bIndex === -1 ? Infinity : bIndex
        return aIndex - bIndex
      })
  }

  set sortedPieFields (newArray) {
    const sortedItems = this.piechartItems.slice().sort((a, b) => {
      const aIndex = newArray.findIndex(i => i.column === a.column)
      const bIndex = newArray.findIndex(i => i.column === b.column)
      return aIndex - bIndex
    })
    this.$typedStore.primary.app.updateConfig({
      configPath: ['piechartItems'],
      value: sortedItems,
    })
  }

  get graphItems () {
    return this.piechartItems
      .map((item) => {
        const title = this.$typedStore.visualisationApp.name(item.column)
        return {
          title: title,
          value: 100,
          color: item.color,
        }
      })
  }

  get showPiechartSidebar () {
    return this.$typedStore.primary.app.config.appConfig.showPiechartSidebar
  }

  set showPiechartSidebar (newValue) {
    this.updateAppConfig('showPiechartSidebar', newValue)
  }

  get showPopupChart () {
    return this.$typedStore.primary.app.config.appConfig.showPopupChart
  }

  set showPopupChart (newValue) {
    this.updateAppConfig('showPopupChart', newValue)
  }

  get hidePiechartOnRootSubroot () {
    return this.$typedStore.primary.app.config.appConfig.hidePiechartOnRootSubroot
  }

  set hidePiechartOnRootSubroot (newValue) {
    this.updateAppConfig('hidePiechartOnRootSubroot', newValue)
  }

  get hidePiechartOnZeroNegative () {
    return this.$typedStore.primary.app.config.appConfig.hidePiechartOnZeroNegative
  }

  set hidePiechartOnZeroNegative (newValue) {
    this.updateAppConfig('hidePiechartOnZeroNegative', newValue)
  }

  toggleGraph () {
    this.showGraph = false
    this.$nextTick(() => {
      this.showGraph = true
    })
  }

  title (path: string) {
    const name = this.$typedStore.primary.entities.nodes.name(path)
    const label = this.$typedStore.primary.entities.nodes.label(path)
    return label || name
  }

  updateAppConfig (propertyName: string, value: any) {
    this.$typedStore.primary.app.updateConfig({
      configPath: [APP_CONFIG_KEY, propertyName],
      value: value,
    })
  }
}
</script>
